import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';

import { AidAdviceDetail, CropFieldAdviceGroup, TreatmentDetail, TreatmentType } from '@next/arable-lib/advice';
import { Admission } from '@next/arable-lib/crop-aid';
import { AgrNumberPipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { TableColumnTypes, TableComponent, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject, tap } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { PageDetailComponent, PrintPageComponent } from '@next/core-lib/components';
import { LetDirective } from '@ngrx/component';
import { loadLegalRestrictions } from '../../../store/actions';
import {
  selectCropAndCropAids,
  selectUniqueAdvicesWithoutTreatments,
  selectUniqueAdvicesWithTreatments,
} from '../../store/selectors';
import { selectCropYear } from '../../../store/selectors';
import { GeneralInfoComponent } from '../general-info/general-info.component';
import { getCropAids } from '../../store/actions';

interface ReportTableData {
  title: string;
  hectares: string;
}

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  imports: [
    LetDirective,
    PageDetailComponent,
    PrintPageComponent,
    GeneralInfoComponent,
    MatIcon,
    TableComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    LowerCasePipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class ReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  @ViewChild('titleColumn', { static: true }) titleColumn!: TemplateRef<unknown>;
  @ViewChild('hectaresColumn', { static: true }) hectaresColumn!: TemplateRef<unknown>;

  protected readonly aidAdviceColumns = [
    'dosagePerHectare',
    'product',
    'total',
    'admissionNumbers',
    'maxDosage',
    'yearlyDosage',
    'safetyPeriodInDays',
    'intervalInDays',
    'numberOfTimesPerYear',
    'checkboxes',
  ];
  protected customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  protected advicesWithTreatments$ = this.store.pipe(
    select(selectUniqueAdvicesWithTreatments({ treatmentType: TreatmentType.GBM })),
    map((advices) => this.sortAdvices(advices)),
  );
  protected advicesWithoutTreatments$ = this.store.pipe(
    select(selectUniqueAdvicesWithoutTreatments({ treatmentType: TreatmentType.GBM })),
    map((advices) => this.sortAdvices(advices)),
  );
  protected advicesWithTreatmentsTableConfig$ = this.generateTableConfig(this.advicesWithTreatments$);
  protected advicesWithoutTreatmentsTableConfig$ = this.generateTableConfig(this.advicesWithoutTreatments$);
  protected pageTitle$ = combineLatest([this.customer$, this.store.select(selectCropYear)]).pipe(
    map(([customer, cropYear]) =>
      `${customer?.name || ''} ${this.locale.translate('report.createGBMAdviceReport')} ${cropYear}`,
    ),
  );
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  protected onCropAndCropAidsLoaded$ = this.store.select(selectCropAndCropAids).pipe(
    filter((cropAndCropAids) => cropAndCropAids.length > 0),
    tap((cropAndCropAids) => this.store.dispatch(loadLegalRestrictions({ cropAndCropAids }))),
    takeUntil(this.destroyed$),
  ).subscribe();

  ngOnInit(): void {
    this.store.dispatch(getCropAids());
    this.updatePageTitle();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    this.onCropAndCropAidsLoaded$.unsubscribe();
  }

  protected print(): void {
    this.window?.print();
  }

  protected back(): void {
    // noinspection JSIgnoredPromiseFromCall
    this.router.navigate(['/']);
  }

  protected getAdmissionNumberString(admissions: Admission[]): string {
    if (!admissions || admissions.length < 1) {
      return '';
    }

    return `${admissions.map((admission) => admission.number).join(', ')}`;
  }

  protected getTreatmentAmount(
    advice: CropFieldAdviceGroup,
    treatment: TreatmentDetail,
    aidAdvice: AidAdviceDetail,
  ): number {
    return (
      aidAdvice.dosage *
      (treatment.shouldUseAreaInHectaresManual
        ? treatment.areaInHectaresManual
        : advice.totalHectares)
    );
  }

  private generateTableConfig(advices$: Observable<CropFieldAdviceGroup[]>): Observable<TableConfig> {
    return advices$.pipe(map((advices) => this.createTableConfig(advices)));
  }

  private updatePageTitle(): void {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) =>
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle })),
    );
  }

  private createTableConfig(advices: CropFieldAdviceGroup[]): TableConfig {
    return {
      // @ts-ignore
      dataSource: new MatTableDataSource(this.getTableData(advices)),
      selectColumn: false,
      pagination: false,
      header: true,
      footer: false,
      disableCards: true,
      columns: [
        {
          id: 'title',
          label: this.locale.translate('report.title'),
          type: TableColumnTypes.TEMPLATE,
          template: this.titleColumn,
        },
        {
          id: 'hectares',
          label: this.locale.translate('report.hectares'),
          className: 'hectares',
          headerClassName: 'hectares',
          type: TableColumnTypes.TEMPLATE,
          template: this.hectaresColumn,
        },
      ],
    };
  }

  private getTableData(advices: CropFieldAdviceGroup[]): ReportTableData[] {
    let totalHectares = 0;
    const dataSource: ReportTableData[] = [];

    advices.forEach((advice) => {
      const title = `${advice.cropName}, ${advice.cropFieldAdvices.length > 1
        ? `${advice.cropFieldAdvices.length} ${this.locale.translate('report.cropFields')}:`
        : advice.cropFieldAdvices[0]?.name}`;

      dataSource.push({
        title,
        hectares: `<div class="spacing--padding-right">${advice.totalHectares?.toFixed(2)}</div>`,
      });

      if (advice.cropFieldAdvices.length > 1) {
        advice.cropFieldAdvices.forEach((cropFieldAdvice) => {
          totalHectares += cropFieldAdvice.areaInHectares;
          dataSource.push({
            title: `<div class="spacing--padding-left text-color--gray3">${cropFieldAdvice.name}</div>`,
            hectares: `<div class="text--right text-color--gray3">${cropFieldAdvice.areaInHectares?.toFixed(2)}</div>`,
          });
        });
      } else {
        totalHectares += advice.totalHectares;
      }
    });

    if (dataSource.length) {
      dataSource.push({
        title: `<div class="total">${this.locale.translate('report.total')}</div>`,
        hectares: `<div class="spacing--padding-right total">${totalHectares.toFixed(2)}</div>`,
      });
    }

    return dataSource;
  }

  private sortAdvices(advices: CropFieldAdviceGroup[]): CropFieldAdviceGroup[] {
    return advices.sort((a, b) => {
      const textA = this.getSortText(a);
      const textB = this.getSortText(b);
      return textA.toLowerCase() < textB.toLowerCase() ? -1 : 1;
    });
  }

  private getSortText(item: CropFieldAdviceGroup): string {
    const cropFieldName = this.cropFieldNameFunc(item);
    const cropName = this.cropNameFunc(item);
    return `${cropName} ${cropFieldName}`;
  }

  private readonly cropFieldNameFunc = (advice: CropFieldAdviceGroup) =>
    advice.cropFieldAdvices.length > 0 ? advice.cropFieldAdvices[0].name : '';

  private readonly cropNameFunc = (advice: CropFieldAdviceGroup) => `${advice.cropName} ${advice.cropVarietyName}`;
}
